/* eslint-disable max-len */
import { getBFFApiClient } from '@acp/feature/auth';
import {
    APIResponse, EnrolmentV1PayloadV1, PropertyRequest,
    PropertyRequestStateWorkflow, PropertyRequestWorkflow, ValidationResponseError,
    PropertyRequestMinimumCreationDetails,
    PropertyEnrolmentResponse,
    LegislationUpgradeType,
    LegislationUpgradeSummary,
    LegislationOfferData,
    LegislationOfferBasic,
    PropertyRequestDocument,
    AgencyPricingPackageMovements,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';

async function getPropertyRequestById(propertyRequestId: string) {
    const response = await getBFFApiClient().get<APIResponse<PropertyRequest>>(
        `/v1/property-request/${propertyRequestId}`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get property request: ${response.data.error}`);
    }

    return response.data.data;
}

async function getPreflightWorkflows(params: PropertyRequestMinimumCreationDetails) {
    const response = await getBFFApiClient().post<APIResponse<PropertyRequestStateWorkflow[]>>(
        '/v1/property-request/preflight-workflows',
        params
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get property request preflight workflows: ${response.data.error}`);
    }

    return response.data.data;
}

async function createPropertyRequest(params: {
    stateWorkflowId: string;
    payloadVersionId: string;
} & PropertyRequestMinimumCreationDetails) {
    const response = await getBFFApiClient().post<APIResponse<PropertyRequest>>(
        '/v1/property-request',
        params
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to create property request: ${response.data.error}`);
    }

    return response.data.data;
}

async function getPropertyRequestWorkflowById(propertyRequestId: string) {
    const response = await getBFFApiClient().get<APIResponse<PropertyRequestWorkflow | null>>(
        `/v1/property-request/${propertyRequestId}/workflow`
    );

    if (
        response.status !== HttpStatusCodes.OK
        || !response.data.success
    ) {
        throw new Error(`Failed to get property request workflow: ${response.data.error}`);
    }

    if (response.data.data === null) {
        throw new Error('This property request has not been assigned a workflow yet.');
    }

    return response.data.data;
}

async function updatePropertyRequestWorkflowPayloadById<TPayload, TFullPayload>(
    propertyRequestId: string,
    payload: TPayload
) {
    const response = await getBFFApiClient().patch<APIResponse<TFullPayload>>(
        `/v1/property-request/${propertyRequestId}/workflow/payload`,
        payload
    );

    ValidationResponseError.throwIfMatches(response);

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`${response.data.error}`);
    }

    return response.data.data;
}

async function submitPropertyRequestWorkflowById(
    propertyRequestId: string
) {
    const response = await getBFFApiClient().post<APIResponse<EnrolmentV1PayloadV1>>(
        `/v1/property-request/${propertyRequestId}/workflow/submit`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`${response.data.error}`);
    }

    return response.data.data;
}

async function getPropertyEnrolmentDetails(propertyRequestId: string, compliancePropertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<PropertyEnrolmentResponse>>(
        `/v1/property-request/${propertyRequestId}/property/enrolment`,
        {
            params: {
                compliancePropertyId,
            },
        }
    );

    if (response.data.success === false) {
        throw new Error(`Error fetching property enrolment details: ${response.data.error}`);
    }

    return response.data.data;
}

async function getLegislationUpgradeOffers(
    upgradeTypeId: number,
    options: {
        propertyId?: number;
        legislationUpgradeTypeId?: number;
    } = {}
) {
    const response = await getBFFApiClient().get<APIResponse<LegislationOfferData[]>>(
        '/v1/legislation/offers',
        {
            params: {
                upgradeTypeId,
                ...options,
            },
        }
    );

    if (response.data.success === false) {
        throw new Error(`Error fetching property enrolment details: ${response.data.error}`);
    }

    return response.data.data;
}

async function getLegislationUpgradeOfferById(upgradeOfferId: number) {
    const response = await getBFFApiClient().get<APIResponse<LegislationOfferBasic>>(
        `/v1/legislation/offers/${upgradeOfferId}`
    );

    if (response.data.success === false) {
        throw new Error(`Error fetching property enrolment details: ${response.data.error}`);
    }

    return response.data.data;
}

async function getLegislationUpgradeTypes() {
    const response = await getBFFApiClient().get<APIResponse<LegislationUpgradeType[]>>(
        '/v1/legislation/upgrade-types'
    );

    if (response.data.success === false) {
        throw new Error(`Error fetching property enrolment details: ${response.data.error}`);
    }

    return response.data.data;
}

async function getPropertyRequestDocuments(propertyRequestId: string, documentIds: string[]) {
    const response = await getBFFApiClient().get<APIResponse<PropertyRequestDocument[]>>(
        `/v1/property-request/${propertyRequestId}/document`,
        {
            params: {
                documentIds,
            },
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get property request documents: ${response.data.error}`);
    }

    return response.data.data;
}

async function calculateLegislationOfferUpgradeSummary(
    upgradeOfferId: number,
    bedrooms: number,
    storeys: number,
    includeRemoteControl: boolean,
    isStudioProperty: boolean
) {
    const response = await getBFFApiClient().get<APIResponse<LegislationUpgradeSummary>>(
        `/v1/legislation/offers/${upgradeOfferId}/summary`,
        {
            params: {
                bedrooms,
                storeys,
                includeRemoteControl,
                isStudioProperty,
            },
        }
    );

    if (response.data.success === false) {
        throw new Error(`Error fetching property enrolment details: ${response.data.error}`);
    }

    return response.data.data;
}

async function getPropertyEnrolmentAvailability(propertyId: number) {
    const response = await getBFFApiClient().get<APIResponse<AgencyPricingPackageMovements>>(
        `/v1/property/${propertyId}/enrolment/availability`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get valid property pricing availability: ${response.data.error}`);
    }

    return response.data.data;
}

export {
    getPropertyRequestById,
    getPreflightWorkflows,
    createPropertyRequest,
    getPropertyRequestWorkflowById,
    updatePropertyRequestWorkflowPayloadById,
    submitPropertyRequestWorkflowById,
    getPropertyEnrolmentDetails,
    getLegislationUpgradeOffers,
    getPropertyRequestDocuments,
    getLegislationUpgradeOfferById,
    getLegislationUpgradeTypes,
    calculateLegislationOfferUpgradeSummary,
    getPropertyEnrolmentAvailability,
};
